import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { ConfigProvider, Drawer } from 'antd';
import { ChatProvider, useChatContext } from '../bot-core/store/ChatContext';
import { BotExtProvider, useBotContext } from './store/BotContext';
import { useBotSession } from './hooks/useBotSession';
import { useChatSocket } from '../bot-core/hooks/useChatSocket';
import useAppDirection from './hooks/useAppDirection';
import { useWhatsappHandoff } from './hooks/useWhatsappHandoff';
import type {
  WhatsappAvailability,
  BotProductType,
} from './hooks/useBotAgentAvailability';
import { buildEmptyStateSubtitle } from './utils/buildEmptyStateSubtitle';
import { botPopupContainer } from './utils/botPopupContainer';
import { FaWhatsapp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ChatButton from '../bot-core/ChatButton';
import ChatHeader from '../bot-core/ChatHeader';
import ChatMessages from '../bot-core/ChatMessages';
import ChatInput from '../bot-core/ChatInput';
import ItinerarySummaryCard from './ItinerarySummaryCard';
import CheckoutCtaButton from './CheckoutCtaButton';
import CheckoutPanel from './CheckoutPanel';
import BotMessageExtras from './BotMessageExtras';
import BotSessionNotice from './BotSessionNotice';
import { useNewSessionCooldown } from './hooks/useNewSessionCooldown';
import { useSendCooldown } from '../bot-core/hooks/useSendCooldown';
import { formatCooldown } from './utils/botSessionLimit';
import { UIMessage } from '../bot-core/types/chat.types';
import { IoBagHandleOutline } from 'react-icons/io5';
import { Tabs } from 'antd';
import {
  useBotCheckoutHandler,
  extractBotSessionId,
} from './hooks/useBotCheckoutHandler';
import { handleBotTurnComplete } from './hooks/handleBotTurnComplete';
import '../bot-core/chat.less';
import './bot.less';
import './drawer-bot.less';

const MIN_WIDTH = 300;
const MAX_WIDTH_RATIO = 0.7;
const DEFAULT_WIDTH_RATIO = 0.4;

interface DrawerBotWidgetInnerProps {
  whatsapp?: WhatsappAvailability | null;
  enabledProducts?: BotProductType[] | null;
}

const DrawerBotWidgetInner: React.FC<DrawerBotWidgetInnerProps> = ({
  whatsapp,
  enabledProducts,
}) => {
  const { state: chatState, dispatch: chatDispatch } = useChatContext();
  const { botState, botDispatch } = useBotContext();

  // useBotSession runs before useChatSocket (its state feeds onDone), so the
  // socket's cancelTurn is handed over through a ref once both have run.
  const cancelTurnRef = useRef<((sessionId: string) => void) | null>(null);
  const onAbandonSession = useCallback((sessionId: string) => {
    cancelTurnRef.current?.(sessionId);
  }, []);

  const {
    initSession,
    ensureSession,
    startNewChat,
    refreshSelections,
    fetchCheckout,
    tabs,
    activeTabId,
    switchTab,
    closeTab,
  } = useBotSession({ onAbandonSession });

  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;

  // The Drawer portals to document.body, outside the App's dir wrapper —
  // re-apply the app direction so RTL/LTR CSS (e.g. the flight-card plane
  // icon) works inside the chat, and dock the drawer on the reading-end side.
  const direction = useAppDirection();
  const isRtl = direction === 'rtl';

  const [drawerWidth, setDrawerWidth] = useState(() =>
    Math.round(window.innerWidth * DEFAULT_WIDTH_RATIO)
  );
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onDone = useCallback(async (meta: any) => {
    if (!botState.sessionId) return;
    await handleBotTurnComplete(
      botState.sessionId,
      meta,
      refreshSelections,
      fetchCheckout,
      () => !!botState.checkoutPayload || !!botState.checkoutExpiresAt,
    );
  }, [
    botState.sessionId,
    botState.checkoutPayload,
    botState.checkoutExpiresAt,
    refreshSelections,
    fetchCheckout,
  ]);

  const onHandedOff = useCallback(
    (handedSessionId: string) => {
      if (handedSessionId === botState.sessionId) {
        chatDispatch({ type: 'SET_OPEN', isOpen: false });
      }
    },
    [botState.sessionId, chatDispatch],
  );

  // See BotWidget — bot:blocked flips the same sessionStatus the widget already
  // derives `isBlocked` from.
  const onBlocked = useCallback(
    (blockedSessionId?: string) => {
      if (blockedSessionId && blockedSessionId !== botState.sessionId) return;
      botDispatch({ type: 'SET_SESSION_STATUS', status: 'blocked' });
    },
    [botState.sessionId, botDispatch],
  );

  // See BotWidget — an ended session is user-recoverable where a blocked one
  // is not, so it gets its own status and its own notice.
  const onSessionEnded = useCallback(
    (endedSessionId?: string) => {
      if (endedSessionId && endedSessionId !== botState.sessionId) return;
      botDispatch({ type: 'SET_SESSION_STATUS', status: 'archived' });
    },
    [botState.sessionId, botDispatch],
  );

  const { connect, disconnect, sendMessage, bindSession, cancelTurn } = useChatSocket({
    namespace: '/bot-agent',
    onDone,
    onHandedOff,
    onBlocked,
    onSessionEnded,
    disconnectNotice: t?.connectionError,
  });
  cancelTurnRef.current = cancelTurn;

  // One socket serves every chat tab, so the socket has to be told which
  // session is on screen — anything streaming for a different one is dropped
  // instead of landing in this chat.
  useEffect(() => {
    bindSession(botState.sessionId ?? null);
  }, [botState.sessionId, bindSession]);

  const { handoff: triggerWhatsappHandoff, busy: handoffBusy } =
    useWhatsappHandoff();

  useEffect(() => { initSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Connect on open regardless of whether a session exists yet — the gateway
  // only authenticates on connect, it doesn't need a session. Connecting early
  // means the socket is ready by the time the first message is sent (which is
  // also what lazily creates the session), so the send_message emit isn't
  // racing the WebSocket handshake.
  useEffect(() => {
    if (chatState.isOpen) connect();
    else disconnect();
  }, [chatState.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => chatDispatch({ type: 'SET_OPEN', isOpen: !chatState.isOpen });
  const handleClose = () => chatDispatch({ type: 'SET_OPEN', isOpen: false });

  const handleSend = (
    text: string,
    opts?: {
      selection?: {
        book_hash?: string;
        package_room?: {
          room_id?: string | number;
          original_room_id?: string;
          room_selection_key?: string;
          adult?: number;
          child?: number;
          infant?: number;
        };
        event_ticket?: {
          ticket_id?: string | number;
          quantity?: number;
          delivery_method_id?: number;
        };
        flight_pair?: {
          outbound_index: number;
          return_index: number;
        };
      };
    },
  ) => {
    // A new turn is starting — latch the previous turn's option lists as
    // superseded so they collapse once and never flicker back to interactive.
    // (Card selections reach here directly, bypassing ChatInput's dispatch.)
    chatDispatch({ type: 'MARK_LAST_SUPERSEDED' });
    // Lazily create the session on the first message so opening the widget
    // never persists an empty session. ensureSession returns the existing id
    // when one already exists, so subsequent sends just emit.
    void (async () => {
      const sessionId = botState.sessionId ?? (await ensureSession());
      if (!sessionId) return;
      sendMessage({
        session_id: sessionId,
        text,
        ...(tenantId ? { tenant_id: tenantId } : {}),
        ...(opts?.selection ? { selection: opts.selection } : {}),
      });
    })();
  };

  const isBlocked = botState.sessionStatus === 'blocked';
  // Ended sessions 403 every further message — composer off, new-chat offered.
  const isEnded = botState.sessionStatus === 'archived';
  const hasActivity = chatState.messages.length > 0 || botState.selections.length > 0;

  // See BotWidget — per-IP new-session quota, gates the composer only while this
  // chat has no session yet so existing chats stay usable.
  const cooldown = useNewSessionCooldown();
  const cooldownBlocksSend = cooldown.active && !botState.sessionId;

  // See BotWidget — per-session message quota, same disabled-composer + wait
  // countdown treatment as the new-session quota above.
  const sendCooldown = useSendCooldown();
  const waitBlocksSend = cooldownBlocksSend || sendCooldown.active;
  const waitLabel = sendCooldown.active
    ? formatCooldown(sendCooldown.secondsLeft)
    : cooldown.label;

  // See BotWidget.onLinkClick — same bot-checkout interception.
  const runBotCheckout = useBotCheckoutHandler();
  // Wrap the shared handler so every checkout entry point in the drawer (footer
  // CTA + intercepted in-chat links) closes the drawer once the SPA navigation
  // to /checkout has run — the drawer must not linger over the checkout page.
  const handleBotCheckout = useCallback(
    async (sid: string, token?: string) => {
      // Only close on a hand-off that actually navigated. The handler resolves
      // false when the cart is empty or the fetch failed (it shows a toast and
      // stays put) — closing then would drop the user's chat over the page they
      // never left, with no way back to the conversation that built the cart.
      const navigated = await runBotCheckout(sid, token);
      if (navigated) chatDispatch({ type: 'SET_OPEN', isOpen: false });
    },
    [runBotCheckout, chatDispatch],
  );
  const onLinkClick = useCallback(
    (url: string, _e: React.MouseEvent): boolean => {
      const sid = extractBotSessionId(url);
      if (!sid) return false;
      void handleBotCheckout(sid);
      return true;
    },
    [handleBotCheckout],
  );

  const handleRemoveItem = (displayName: string) => {
    handleSend(`Remove ${displayName}`);
  };

  // ── Drag-to-resize ───────────────────────────────────────────────────────────
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = drawerWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      // Drawer docks on the reading-end edge; dragging toward the page
      // centre → larger width (right dock: drag left, left dock: drag right)
      const delta = isRtl
        ? ev.clientX - startX.current
        : startX.current - ev.clientX;
      const raw = startWidth.current + delta;
      const maxWidth = Math.round(window.innerWidth * MAX_WIDTH_RATIO);
      setDrawerWidth(Math.min(Math.max(raw, MIN_WIDTH), maxWidth));
    };

    const onMouseUp = () => {
      isResizing.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [drawerWidth, isRtl]);

  // ── Message extras ───────────────────────────────────────────────────────────
  const renderMessageExtra = (msg: UIMessage) => (
    <BotMessageExtras msg={msg} onSend={handleSend} />
  );

  const showCheckoutCta =
    !!botState.checkoutPayload &&
    !!botState.checkoutExpiresAt &&
    !chatState.isStreaming;
  const checkoutCtaBar = showCheckoutCta ? (
    <div className="bot-checkout-bar">
      <CheckoutCtaButton
        sessionId={botState.checkoutPayload!.session_id}
        expiresAt={botState.checkoutExpiresAt ?? undefined}
        onCheckout={handleBotCheckout}
        label={t?.checkout?.completeBooking}
      />
    </div>
  ) : null;

  const handleToggleCheckoutPanel = useCallback(async () => {
    if (botState.checkoutReady) {
      botDispatch({ type: 'HIDE_CHECKOUT' });
      return;
    }
    if (botState.sessionId && botState.checkoutPayload) {
      await fetchCheckout(botState.sessionId);
    }
    botDispatch({ type: 'SHOW_CHECKOUT' });
  }, [
    botState.checkoutReady,
    botState.sessionId,
    botState.checkoutPayload,
    botDispatch,
    fetchCheckout,
  ]);

  // See BotWidget — same "Preview cart" toggle.
  const hasCheckoutPayload = !!botState.checkoutPayload;
  const showWhatsappHandoff =
    !!whatsapp?.enabled &&
    !!whatsapp?.business_number &&
    !!botState.sessionId;

  const headerActions = (
    <>
      {showWhatsappHandoff && (
        <button
          className="bot-icon-btn"
          // TODO(i18n): translation key bot.handoff.continueOnWhatsapp
          title="Continue on WhatsApp"
          disabled={handoffBusy}
          onClick={() => {
            if (botState.sessionId) triggerWhatsappHandoff(botState.sessionId);
          }}
        >
          <FaWhatsapp size={20} />
        </button>
      )}
      {hasCheckoutPayload && (
        <button
          className={`bot-icon-btn${botState.checkoutReady ? ' bot-icon-btn--active' : ''}`}
          title={t?.previewCart ?? 'Preview cart'}
          onClick={handleToggleCheckoutPanel}
        >
          <IoBagHandleOutline size={20} />
        </button>
      )}
    </>
  );

  // Cache active session's first user message as the tab label.
  const firstUserText = useMemo(() => {
    const m = chatState.messages.find((m) => m.role === 'user');
    return m?.text;
  }, [chatState.messages]);
  useEffect(() => {
    if (activeTabId && firstUserText) {
      botDispatch({
        type: 'SET_TAB_TITLE',
        sessionId: activeTabId,
        title: firstUserText.slice(0, 40),
      });
    }
  }, [activeTabId, firstUserText, botDispatch]);

  // Browser-style tabs (see BotWidget for design notes).
  const newChatLabel = t?.newChat ?? 'New chat';
  const tabItems = tabs.map((tab) => ({
    key: tab.id,
    label:
      (botState.tabTitles[tab.id] && botState.tabTitles[tab.id].trim()) ||
      (tab.title && tab.title.trim()) ||
      newChatLabel,
    children: null,
    closable: true,
  }));
  const tabsStrip = tabs.length > 0 ? (
    <div className="bot-tabs-strip">
      <Tabs
        type="editable-card"
        activeKey={activeTabId ?? undefined}
        items={tabItems}
        // hideAdd={false} // TODO: Add back in when we have a way to create new chats
        hideAdd={true} // TODO: Add back in when we have a way to create new chats
        onChange={(key) => switchTab(key)}
        onEdit={(targetKey, action) => {
          if (action === 'add') {
            startNewChat();
          } else if (action === 'remove' && typeof targetKey === 'string') {
            closeTab(targetKey);
          }
        }}
      />
    </div>
  ) : null;

  return (
    // BotWidgetGate mounts outside App.tsx's <ConfigProvider direction=…>, so
    // antd components in the chat (Drawer, Tabs, …) need their own provider
    // to follow the app direction. getPopupContainer keeps antd overlays
    // (Select, Tooltip, …) inside the drawer's stacking context — the drawer
    // sits at z-index 9998, so body-portaled popups would otherwise render
    // behind it (e.g. the ticket quantity dropdown showing no options).
    <ConfigProvider direction={direction} getPopupContainer={botPopupContainer}>
      {/* FAB — shown only when drawer is closed */}
      {!chatState.isOpen && (
        <ChatButton isOpen={false} onClick={handleToggle} hasActivity={hasActivity} />
      )}

      {/* Drag-to-resize handle — fixed, lines up with drawer's inner edge */}
      {chatState.isOpen && (
        <div
          className="bot-drawer__resize-handle"
          style={isRtl ? { left: drawerWidth } : { right: drawerWidth }}
          onMouseDown={startResize}
        />
      )}

      <Drawer
        className={`bot-drawer direction-${direction}`}
        placement={isRtl ? 'left' : 'right'}
        visible={chatState.isOpen}
        width={drawerWidth}
        mask={false}
        closable={false}
        title={null}
        // Mount inside #App so the drawer inherits the app theme (body-scoped
        // CSS vars, .App font + .App .ant-* overrides). Without an explicit
        // getContainer, antd v4 falls back to getPopupContainer(document.body)
        // for the drawer's own container — which our botPopupContainer resolves
        // to <html>, mounting the drawer outside <body> and stripping all
        // theming. getPopupContainer still routes overlay popups above the drawer.
        getContainer={() => document.getElementById('App') ?? document.body}
        zIndex={9998}
        bodyStyle={{
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          height: '100%',
        }}
        drawerStyle={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Re-apply the app's dir attribute inside the portal (see above) */}
        <div className="bot-drawer__body" dir={direction}>
        <ChatHeader
          title={t?.title ?? 'Sky360 Travel Assistant'}
          avatarEmoji="✈️"
          extraActions={headerActions}
          onClose={handleClose}
        />
        {tabsStrip}
        <ChatMessages
          avatarEmoji="✈️"
          isBlocked={isBlocked}
          emptyStateContent={
            <div className="bot-empty-state">
              <div className="bot-empty-state__icon">✈️</div>
              <div className="bot-empty-state__title">
                {t?.emptyState?.title ?? 'How can I help plan your trip?'}
              </div>
              <div className="bot-empty-state__sub">
                {buildEmptyStateSubtitle(t?.emptyState, enabledProducts)}
              </div>
            </div>
          }
          blockedMessage={t?.blocked ?? 'This chat has been disabled.'}
          // extraBottom={
          //   botState.selections.length > 0
          //     ? <ItinerarySummaryCard onRemove={handleRemoveItem} />
          //     : null
          // }
          renderMessageExtra={renderMessageExtra}
          onLinkClick={onLinkClick}
        />
        {checkoutCtaBar}
        <BotSessionNotice
          t={t?.sessionLimit}
          cooldownActive={cooldownBlocksSend}
          cooldownLabel={cooldown.label}
          cooldownJustEnded={cooldown.justEnded && !botState.sessionId}
          genericError={botState.sessionCreateFailed}
          hasOtherChats={tabs.length > 0}
          sessionEnded={isEnded}
          onStartNewChat={startNewChat}
        />
        <ChatInput
          onSend={handleSend}
          isBlocked={isBlocked || isEnded || waitBlocksSend}
          placeholder={t?.input?.placeholder ?? 'Ask me anything about your trip…'}
          blockedPlaceholder={
            !isBlocked && waitBlocksSend
              ? (t?.sessionLimit?.inputPlaceholder ?? 'Please wait {{time}}…').replace(
                  '{{time}}',
                  waitLabel,
                )
              : (t?.input?.disabled ?? 'Chat disabled')
          }
          starterReplies={t?.emptyState?.starters}
        />
        {botState.checkoutReady && <CheckoutPanel />}
        </div>
      </Drawer>
    </ConfigProvider>
  );
};

interface DrawerBotWidgetProps {
  whatsapp?: WhatsappAvailability | null;
  enabledProducts?: BotProductType[] | null;
}

const DrawerBotWidget: React.FC<DrawerBotWidgetProps> = ({
  whatsapp,
  enabledProducts,
}) => (
  <ChatProvider>
    <BotExtProvider>
      <DrawerBotWidgetInner
        whatsapp={whatsapp}
        enabledProducts={enabledProducts}
      />
    </BotExtProvider>
  </ChatProvider>
);

export default DrawerBotWidget;
